import { Injectable } from '@angular/core';
import { MatDialogConfig, MatDialogRef } from '@angular/material/dialog'
import { IAppDashboard, IAppDashboardMenu, IPluginPermissions } from '../models'
import { IMenu, IMenuItem } from './menu/menu.interface'
import { IApplicationPlugin } from './application-plugin.interface'
import { MenuService } from './menu/menu.service'
import { PermissionService } from './permissions/permission.service'
import { WidgetService } from './widget/widget.service'
import { IdleService } from './idle-service/idle.service'
import { PluginEventService } from './plugin-event-service/plugin-event.service'

@Injectable({
  providedIn: 'root'
})
export class PluginInjectorService {
  private readonly plugins: IApplicationPlugin[] = []

  constructor(
    public permissionService: PermissionService,
    private readonly menuService: MenuService,
    public widgetService: WidgetService,
    public idleService: IdleService,
    public pluginEventService: PluginEventService
  ) {}

  /**
   * plugin methods
   */
  register(plugin: IApplicationPlugin): void {
    this.plugins.push(plugin)
  }

  getPlugins(): IApplicationPlugin[] {
    return this.plugins
  }

  getPluginByName(pluginName: string): IApplicationPlugin | undefined {
    return this.plugins.find((plugin: IApplicationPlugin) => plugin.name === pluginName)
  }

  getPluginById(pluginId: string): IApplicationPlugin | undefined {
    return this.plugins.find((plugin: IApplicationPlugin) => plugin._id === pluginId)
  }

  updatePluginStatus(pluginId: string, enabled: boolean) {
    const pluginIndex = this.plugins.findIndex((plugin: IApplicationPlugin) => plugin._id === pluginId)
    this.plugins[pluginIndex].enabled = enabled
  }

  /**
   * menu methods
   */
  getAppDashboardMenu(permissions: IPluginPermissions[]): IAppDashboardMenu[] {
    const menuItems= []

    for (const plugin of this.plugins) {
      if (plugin?.menuItems) {
        for (const item of plugin.menuItems) {
          const hasPermissions = permissions.some((pluginPermission: IPluginPermissions) => pluginPermission.plugin_id === item.pluginId)
          if (hasPermissions) {
            menuItems.push({
              nameIdentifier: item.nameIdentifier,
              primary: {enabled: item.primary.enabled, position: item.primary.position},
              secondary: {enabled: item.secondary.enabled, position: item.secondary.position},
              pluginName: item.pluginName
            })
          }
        }
      }
    }

    return menuItems
  }

  loadPluginMenuItems(appDashboard: IAppDashboard): void {
    this.resetMenu()
    const dashboardMenu: IAppDashboardMenu[] = appDashboard.menu

    const primaryMenus: {pos: number, isCore: boolean, item: IMenuItem}[] = [];
    const secondaryMenus: {pos: number, isCore: boolean, item: IMenuItem}[] = [];

    for (const plugin of this.plugins) {
      if (plugin?.menuItems) {
        const isCore = !!plugin.corePlugin;
        for (const menuItems of plugin.menuItems) {
          const foundMenu = dashboardMenu.find((item: IAppDashboardMenu) => item.nameIdentifier.toLowerCase() === menuItems.nameIdentifier.toLowerCase())
          if (foundMenu) {
            const userDashboardMenu: IMenuItem = {...menuItems, ...foundMenu}
            if (userDashboardMenu.primary.enabled && userDashboardMenu.primary.position != null) {
              primaryMenus.push({pos: userDashboardMenu.primary.position, isCore, item: userDashboardMenu})
            }

            if (userDashboardMenu.secondary.enabled && userDashboardMenu.secondary.position != null) {
              secondaryMenus.push({pos: userDashboardMenu.secondary.position, isCore, item: userDashboardMenu})
            }
          }
        }
      }
    }

    // External-plugin items always render leftmost (before core-plugin items).
    // Items with headerSortOrder have a fixed position within their group (lower = further left,
    // higher = further right); items without it sort by DB position (higher pos = further left).
    const menuSort = (a: {pos: number, isCore: boolean, item: IMenuItem}, b: {pos: number, isCore: boolean, item: IMenuItem}) => {
      if (a.isCore !== b.isCore) return a.isCore ? 1 : -1;
      const hasOrderA = a.item.headerSortOrder != null;
      const hasOrderB = b.item.headerSortOrder != null;
      // Items with a fixed order always sort to the right of items without one.
      if (hasOrderA !== hasOrderB) return hasOrderA ? 1 : -1;
      if (hasOrderA && hasOrderB) return (a.item.headerSortOrder!) - (b.item.headerSortOrder!);
      return b.pos - a.pos;
    };
    primaryMenus.sort(menuSort);
    secondaryMenus.sort(menuSort);
    secondaryMenus.reverse();

    this.menuService._setMenu({
      primary: primaryMenus.map(menu => menu.item),
      secondary: secondaryMenus.map(menu => menu.item)
    });
  }

  addMenuItemOptions(menuItem: IMenuItem): void {
    this.menuService._addToItemList(menuItem)
  }

  getMenuItemOptions(): IMenuItem[] {
    return this.menuService._getItemList()
  }

  getMenu(): IMenu {
    return this.menuService._getMenu()
  }

  setMenu(menu: IMenu): void {
    this.menuService._setMenu(menu);
  }

  getMenuDialogConfig(): MatDialogConfig {
    return this.menuService._getDialogConfig()
  }

  setMenuDialogRef(value: MatDialogRef<any>): void {
    this.menuService.openDialogRefSubject$.next(value)
  }

  resetMenu() {
    this.menuService._resetItemList()
    this.menuService._resetMenu()
  }
}
